<template>
  <b-modal
    :visible="visible"
    title="Login & MFA Help"
    size="lg"
    scrollable
    ok-only
    ok-title="Close"
    @hidden="$emit('hidden')"
  >
    <h6>Logging in</h6>
    <ol>
      <li>Enter your email and password.</li>
      <li>
        Enter the 6-digit code from your authenticator app.
        <strong>Enter it promptly</strong> — the login session expires after about 10 minutes.
        If you see <em>"Invalid session for the user, session is expired"</em>, start the login again.
      </li>
      <li>If you have multiple accounts, select the one you want.</li>
    </ol>

    <h6>First-time MFA setup</h6>
    <p>You'll be asked to set up multi-factor authentication after your first login.</p>
    <ol>
      <li>Install an authenticator app (Google Authenticator, Authy, Microsoft Authenticator, 1Password).</li>
      <li>Scan the <strong>QR code</strong> on the setup screen — or copy the <strong>Secret Key</strong> and add it manually.</li>
      <li>Enter the 6-digit code the app shows and click <strong>Verify Code</strong>.</li>
    </ol>
    <p class="mb-1">
      <strong>Tips for a smooth setup:</strong>
    </p>
    <ul>
      <li>Scan the QR code that is <strong>currently on screen</strong> and complete the setup in one sitting — don't refresh the page between scanning and verifying.</li>
      <li>Made several attempts? Your authenticator app may have multiple "RingbaX" entries. Delete <strong>all</strong> of them, refresh the page once, scan the fresh QR code, and verify.</li>
      <li>Save the <strong>Secret Key</strong> in a password manager — with it you can re-add RingbaX to any authenticator app later, even on a new phone.</li>
    </ul>

    <h6>Forgot password</h6>
    <ol>
      <li>On the login page, click <strong>Forgot Password</strong>.</li>
      <li>Enter your email and submit, then check your email for a verification code (look in spam too).</li>
      <li>Enter the code and your new password, then log in with the new password.</li>
    </ol>

    <h6>Common issues</h6>
    <p class="mb-1">
      <strong>"Not authorized, error: ..."</strong> — the text after "error:" tells you the cause:
    </p>
    <ul>
      <li><em>Incorrect username or password</em> — re-type carefully; use Forgot Password if unsure.</li>
      <li><em>Password attempts exceeded</em> — too many failed tries; wait about 15 minutes and try again.</li>
      <li><em>Temporary password has expired and must be reset by an administrator</em> — contact support for a new temporary password.</li>
      <li><em>User is disabled</em> — contact support.</li>
      <li><em>Invalid session for the user, session is expired</em> — you took too long at the code step; log in again and enter the code within about 10 minutes.</li>
    </ul>

    <p class="mb-1">
      <strong>MFA code rejected during setup</strong>
    </p>
    <ul>
      <li>The code must come from the QR code <strong>currently on screen</strong> — codes from a previously scanned QR won't work. Delete old "RingbaX" entries, refresh the page, scan again.</li>
      <li>Check your phone's clock is set to automatic — codes fail if the device time is off.</li>
    </ul>

    <p class="mb-1">
      <strong>MFA code rejected during login</strong>
    </p>
    <ul>
      <li>Wait for the current code to expire and enter the <strong>next</strong> one.</li>
      <li>Verify your phone's time is set to automatic.</li>
      <li>If it never works, your authenticator may hold a stale "RingbaX" entry from an abandoned setup — contact support to reset MFA on your account.</li>
    </ul>

    <p class="mb-1">
      <strong>Lost your phone / new device</strong>
    </p>
    <ul>
      <li>If your authenticator app backs up entries (Authy, 1Password, Google Authenticator with account sync), restore it on the new device and log in as usual.</li>
      <li>No backup? Contact support and ask for an <strong>MFA reset</strong> — you'll go through MFA setup again on your next login.</li>
    </ul>

    <p class="mb-1">
      <strong>Still stuck?</strong>
    </p>
    <p class="mb-0">
      Do a hard refresh (Ctrl+F5 / Cmd+Shift+R) or try an incognito/private window.
      If the problem persists, contact support with your email, the exact error text (or a screenshot),
      and the step you were on — "not authorized" alone isn't enough to diagnose.
    </p>
  </b-modal>
</template>

<script>
export default {
  name: 'LoginHelpModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
  },
}
</script>

<style>

</style>
